// Transaction Wizard — scans arriving faster than they are processed.
//
// A hardware (keyboard-wedge) scanner types a code and Enter in a few
// milliseconds; a picker can fire the next label before the server has parsed
// the last one. Every scan is processed, in the order it was read: a scan that
// arrives while another is in flight waits its turn (FIFO) — never dropped,
// never run alongside the one before it (each reads the record the previous
// one left).

export interface SerialQueue<T, R> {
  /** Queue `item`; resolves with its own result once it has been processed. */
  push(item: T): Promise<R>
  /** Items waiting or running. */
  size(): number
  /** Forget what has not started yet (their promises resolve with `idle`). */
  clear(): void
}

export function createSerialQueue<T, R>(
  process: (item: T) => Promise<R>,
  /** What a cleared or failed item resolves with. */
  idle: R,
): SerialQueue<T, R> {
  const waiting: Array<{ item: T; resolve: (r: R) => void }> = []
  let running = false

  const pump = async () => {
    if (running) return
    running = true
    try {
      while (waiting.length) {
        const next = waiting.shift()!
        let out = idle
        try {
          out = await process(next.item)
        } catch {
          out = idle
        }
        next.resolve(out)
      }
    } finally {
      running = false
    }
  }

  return {
    push(item: T) {
      return new Promise<R>((resolve) => {
        waiting.push({ item, resolve })
        void pump()
      })
    },
    size: () => waiting.length + (running ? 1 : 0),
    clear() {
      for (const w of waiting.splice(0)) w.resolve(idle)
    },
  }
}
